// Renders the `math` render_spec kind with KaTeX.
//
// WHY THIS EXISTS: competition papers (AMC) carry real notation -- superscripts,
// fractions, radicals -- and the extractor cannot flatten them into `stem_text`
// without corrupting them. "8^3 x 3^6 / 6^5" flattened reads "83 x 36 / 65",
// which is wrong but still looks like a number, so nothing downstream can catch
// it. The equation therefore travels as LaTeX in the render_spec, exactly like a
// tally table or a balance scale travels as structured data, and is drawn here.
//
// NAPLAN never needed this (Year 3-9 numeracy has no algebraic notation), which
// is why the other 21 spec kinds are all diagram primitives.
//
// SAFETY: `trust: false` and `strict: false` mean KaTeX will not expand \href or
// other escape-hatch commands, and unknown commands render as visible errors
// rather than throwing. Bank content is ours, but it is machine-extracted from
// third-party PDFs, so it is treated as untrusted input.
import katex from 'katex';
import 'katex/dist/katex.min.css';
import 'katex/contrib/mhchem';
import { useMemo } from 'react';

export function AcademyMath({
  latex,
  display = false,
  className,
}: {
  latex: string;
  display?: boolean;
  className?: string;
}) {
  const html = useMemo(() => {
    try {
      return katex.renderToString(latex, {
        displayMode: display,
        throwOnError: false,
        trust: false,
        strict: false,
        output: 'htmlAndMathml',
      });
    } catch {
      return null;
    }
  }, [latex, display]);

  // A LaTeX string KaTeX cannot parse at all is shown verbatim rather than
  // dropped: a moderator needs to see that the extraction produced something
  // unrenderable, not an empty cell that looks like a question with no content.
  if (!html) {
    return (
      <code className={className} data-testid="academy-math-raw">
        {latex}
      </code>
    );
  }

  return (
    <span
      className={className}
      data-testid="academy-math"
      // KaTeX output is generated by KaTeX itself from the string above, with
      // trust disabled, so it contains no author-supplied markup or URLs.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
